import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useEventManageContext } from './EventManagePage';
import { BarChart3, RefreshCw } from 'lucide-react';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { getRequestsByEventId, getRankingsByEventId, SongRequest, AttendeeRanking } from '../../services/requests';
import { getEventAnalytics, refreshEventAnalytics } from '../../services/analytics';
import { getEventAwards, type EventAwards } from '../../services/eventAwards';
import EventAwardsCard from '../../components/games/EventAwardsCard';
import { computeEventAnalytics, computeAttendeeProfiles, computeGenreChartData, withLiveTimeline } from '../../utils/analyticsUtils';
import { isAttendeeCancelled } from '../../utils/requestCancel';
import type { EventAnalytics, VoteRecord } from '../../types/analytics';
import toast from 'react-hot-toast';

// Sub-components
import EventSummaryCard from '../../components/analytics/EventSummaryCard';
import EngagementTimeline from '../../components/analytics/EngagementTimeline';
import GenreBreakdown from '../../components/analytics/GenreBreakdown';
import TopPerformers from '../../components/analytics/TopPerformers';
import VotingPatterns from '../../components/analytics/VotingPatterns';
import QueueHealth from '../../components/analytics/QueueHealth';
import SpotifyMetrics from '../../components/analytics/SpotifyMetrics';
import AttendeeProfiles from '../../components/analytics/AttendeeProfiles';
import ComparativeAnalytics from '../../components/analytics/ComparativeAnalytics';
import AnalyticsExport from '../../components/analytics/AnalyticsExport';

export default function AnalyticsPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { event } = useEventManageContext();
  const [requests, setRequests] = useState<SongRequest[]>([]);
  const [votes, setVotes] = useState<VoteRecord[]>([]);
  const [rankings, setRankings] = useState<AttendeeRanking[]>([]);
  const [verifiedOderIds, setVerifiedOderIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [cachedAnalytics, setCachedAnalytics] = useState<EventAnalytics | null>(null);
  const [eventAwards, setEventAwards] = useState<EventAwards | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Event awards (ITER-041): read on their own so a failure here never breaks the rest of the page
  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    getEventAwards(eventId)
      .then((awards) => { if (!cancelled) setEventAwards(awards); })
      .catch((error) => console.error('[Analytics] Loading event awards failed:', error));
    return () => { cancelled = true; };
  }, [eventId]);

  // Load all data
  useEffect(() => {
    const loadData = async () => {
      if (!eventId) return;

      // For completed events, try to load cached analytics first
      // A refused or failed read counts as no saved copy, and the numbers are worked out live instead
      if (event?.status === 'completed') {
        try {
          const cached = await getEventAnalytics(eventId);
          if (cached) setCachedAnalytics(cached);
        } catch (error) {
          console.error('[Analytics] Reading the saved analytics failed; computing them live instead:', error);
        }
      }

      // Always load raw data (needed for exports + attendee profiles)
      const [eventRequests, eventRankings] = await Promise.all([
        getRequestsByEventId(eventId),
        getRankingsByEventId(eventId),
      ]);
      setRequests(eventRequests.filter(r => !isAttendeeCancelled(r)));
      setRankings(eventRankings);

      // Load votes
      const votesQuery = query(collection(db, 'votes'), where('eventId', '==', eventId));
      const votesSnap = await getDocs(votesQuery);
      const voteRecords: VoteRecord[] = votesSnap.docs.map(d => {
        const data = d.data();
        const votedAt = data.votedAt instanceof Timestamp
          ? data.votedAt.toDate().toISOString()
          : data.votedAt || '';
        return {
          oderId: data.oderId || '',
          requestId: data.requestId || '',
          type: data.type || 'up',
          votedAt,
        };
      });
      setVotes(voteRecords);

      // Load verified attendees
      const verifiedQuery = query(collection(db, 'verifiedAttendees'), where('eventId', '==', eventId));
      const verifiedSnap = await getDocs(verifiedQuery);
      const vIds = new Set<string>();
      verifiedSnap.docs.forEach(d => {
        const oid = d.data().oderId;
        if (oid) vIds.add(oid);
      });
      setVerifiedOderIds(vIds);

      setLoading(false);
    };
    loadData();
  }, [eventId, event?.status]);

  // Compute analytics: use cached for completed events, compute live for active events
  // The saved timeline is bucketed in the server's UTC hours, so it is always rebuilt here in local time
  const analytics: EventAnalytics | null = useMemo(() => {
    if (cachedAnalytics && event?.status === 'completed') return withLiveTimeline(cachedAnalytics, requests, votes);
    if (!event || requests.length === 0) return null;
    const computed = computeEventAnalytics(requests, votes, rankings, event);
    return { ...computed, computedAt: new Date().toISOString() };
  }, [cachedAnalytics, event, requests, votes, rankings]);

  // Attendee profiles (always computed from raw data for live updates)
  const attendeeProfiles = useMemo(() => {
    if (loading) return [];
    return computeAttendeeProfiles(requests, votes, rankings, verifiedOderIds);
  }, [loading, requests, votes, rankings, verifiedOderIds]);

  // Genre chart data
  const genreChartData = useMemo(() => computeGenreChartData(requests), [requests]);

  // Event-only attendees (joined at or after event start time)
  const eventOnlyAttendees = useMemo(() => {
    if (!event?.date || !event?.startTime || attendeeProfiles.length === 0) return 0;
    const [y, m, d] = event.date.split('-').map(Number);
    const [hh, mm] = event.startTime.split(':').map(Number);
    const eventStartMs = new Date(y, m - 1, d, hh, mm).getTime();
    return attendeeProfiles.filter(p => {
      if (!p.firstActiveTime) return false;
      return new Date(p.firstActiveTime).getTime() >= eventStartMs;
    }).length;
  }, [event, attendeeProfiles]);

  // Unverified played count
  const unverifiedPlayedCount = useMemo(() => {
    if (verifiedOderIds.size === 0 && !event?.settings?.onSiteVerificationEnabled) return -1;
    const played = requests.filter(r => r.status === 'played');
    return played.filter(r => !verifiedOderIds.has(r.requestedBy.oderId)).length;
  }, [requests, verifiedOderIds, event?.settings?.onSiteVerificationEnabled]);

  // Refresh analytics (for completed events — the server re-computes and saves, then the saved copy is re-read)
  const handleRefresh = async () => {
    if (!eventId) return;
    setRefreshing(true);
    try {
      await refreshEventAnalytics(eventId);
      const saved = await getEventAnalytics(eventId);
      if (!saved) throw new Error('Saved analytics missing after refresh');
      setCachedAnalytics(saved);
      toast.success('Analytics refreshed and saved');
    } catch (error) {
      console.error('[Analytics] Refresh failed:', error);
      toast.error('Could not refresh analytics. Try again in a moment.');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return <div className="animate-pulse">Loading analytics...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Analytics
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {event?.status === 'completed' ? 'Post-event analytics' : 'Live event analytics'}
            {cachedAnalytics && (
              <span className="ml-2 text-xs text-green-500">(cached)</span>
            )}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {event?.status === 'completed' && (
            <button onClick={handleRefresh} disabled={refreshing} className="btn btn-secondary text-sm" title="Refresh cached analytics">
              <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </button>
          )}
          <AnalyticsExport
            requests={requests}
            analytics={analytics}
            event={event}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
          />
        </div>
      </div>

      {/* Summary Cards */}
      {analytics && (
        <EventSummaryCard
          summary={analytics.summary}
          topGenre={analytics.discovery.topGenre}
          eventOnlyAttendees={eventOnlyAttendees}
          unverifiedPlayedCount={unverifiedPlayedCount}
          showVerification={!!event?.settings?.onSiteVerificationEnabled}
          showEventOnly={!!event?.startTime}
          verifiedCount={verifiedOderIds.size}
          unverifiedCount={attendeeProfiles.length - verifiedOderIds.size}
          postEventData={event?.postEventData}
        />
      )}

      {/* Event Awards (ITER-041) */}
      {eventAwards && eventAwards.revealed.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Event Awards</h2>
          <EventAwardsCard awards={eventAwards} />
        </section>
      )}

      {/* Charts Row: Timeline + Genre */}
      <div className="grid md:grid-cols-2 gap-6">
        {analytics && (
          <EngagementTimeline
            timeline={analytics.timeline}
            peakBucket={analytics.peaks.peakCombinedBucket}
          />
        )}
        <GenreBreakdown data={genreChartData} />
      </div>

      {/* Top Songs */}
      <TopPerformers requests={requests} />

      {/* Voting Patterns + Engagement Funnel */}
      {analytics && (
        <VotingPatterns summary={analytics.summary} timeline={analytics.timeline} />
      )}

      {/* Queue Health */}
      {analytics && (
        <QueueHealth queue={analytics.queue} />
      )}

      {/* Hidden: Spotify API integration paused — Spotify & Discovery Metrics */}
      {false && analytics && (
        <SpotifyMetrics
          discovery={analytics!.discovery}
          totalRequests={analytics!.summary.totalRequests}
        />
      )}

      {/* Attendee Profiles */}
      <AttendeeProfiles profiles={attendeeProfiles} requests={requests} votes={votes} />

      {/* Cross-Event Comparison */}
      {event?.djId && (
        <ComparativeAnalytics
          currentAnalytics={analytics}
          djId={event.djId}
          currentEventId={eventId || ''}
        />
      )}
    </div>
  );
}
